import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragStartEvent, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { useStoreList, getStoreColor } from "@/hooks/useStoreList";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  GripVertical,
  Pencil,
  Trash2,
  CreditCard,
  QrCode,
  Truck,
  Wallet,
} from "lucide-react";
import {
  usePaymentMethods,
  useUpdatePaymentMethod,
  useDeletePaymentMethod,
  useReorderPaymentMethods,
  type PaymentMethod,
} from "./hooks";

const TYPE_CONFIG: Record<
  string,
  { icon: typeof CreditCard; label: string; color: string; bg: string }
> = {
  "Bank Transfer": {
    icon: QrCode,
    label: "Bank Transfer / PromptPay",
    color: "text-blue-600",
    bg: "bg-blue-50 dark:bg-blue-950/30",
  },
  Stripe: {
    icon: CreditCard,
    label: "Stripe",
    color: "text-purple-600",
    bg: "bg-purple-50 dark:bg-purple-950/30",
  },
  COD: {
    icon: Truck,
    label: "Cash on Delivery",
    color: "text-amber-600",
    bg: "bg-amber-50 dark:bg-amber-950/30",
  },
  PaySolution: {
    icon: Wallet,
    label: "PaySolution",
    color: "text-teal-600",
    bg: "bg-teal-50 dark:bg-teal-950/30",
  },
};

interface PaymentMethodListProps {
  onEdit: (method: PaymentMethod) => void;
}

export function PaymentMethodList({ onEdit }: PaymentMethodListProps) {
  const { data: rawMethods, isLoading } = usePaymentMethods();
  const methods: PaymentMethod[] = Array.isArray(rawMethods) ? rawMethods : [];
  const updateMutation = useUpdatePaymentMethod();
  const deleteMutation = useDeletePaymentMethod();
  const reorderMutation = useReorderPaymentMethods();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null);
  const [orderedMethods, setOrderedMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    if (methods && !activeId) {
      const sorted = [...methods].sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
      );
      setOrderedMethods(sorted);
    }
  }, [methods, activeId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = orderedMethods.findIndex((m) => m.name === active.id);
    const newIndex = orderedMethods.findIndex((m) => m.name === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedMethods, oldIndex, newIndex);
    setOrderedMethods(newOrder);

    reorderMutation.mutate(
      newOrder.map((m, i) => ({ name: m.name, display_order: i }))
    );
  }

  function handleToggleEnabled(method: PaymentMethod) {
    updateMutation.mutate({
      name: method.name,
      data: { enabled: method.enabled ? 0 : 1 },
    });
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.name, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="p-6 space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-md bg-muted/50"
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!orderedMethods.length) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
            <CreditCard className="h-6 w-6 text-muted-foreground/50" />
          </div>
          <div>
            <p className="font-medium text-sm">No payment methods</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add a payment method to start accepting payments on your storefront.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeMethod = activeId
    ? orderedMethods.find((m) => m.name === activeId)
    : null;

  return (
    <>
      <Card className="rounded-2xl border-0">
        <CardContent className="p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedMethods.map((m) => m.name)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {orderedMethods.map((method) => (
                  <SortableMethodCard
                    key={method.name}
                    method={method}
                    onEdit={() => onEdit(method)}
                    onDelete={() => setDeleteTarget(method)}
                    onToggle={() => handleToggleEnabled(method)}
                    isActive={activeId === method.name}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeMethod ? (
                <MethodCardContent
                  method={activeMethod}
                  className="shadow-2xl ring-2 ring-primary/20"
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Delete Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>
                {deleteTarget
                  ? TYPE_CONFIG[deleteTarget.payment_type]?.label ??
                    deleteTarget.payment_type
                  : ""}
              </strong>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
              className="rounded"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="rounded"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- Sortable card wrapper ---

function SortableMethodCard({
  method,
  onEdit,
  onDelete,
  onToggle,
  isActive,
}: {
  method: PaymentMethod;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  isActive: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: method.name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <MethodCardContent
        method={method}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggle={onToggle}
        dragHandleProps={{ ...attributes, ...listeners }}
        className={isActive ? "ring-2 ring-primary/20" : ""}
      />
    </div>
  );
}

// --- Card content (shared between sortable and overlay) ---

function MethodCardContent({
  method,
  onEdit,
  onDelete,
  onToggle,
  dragHandleProps,
  className = "",
}: {
  method: PaymentMethod;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggle?: () => void;
  dragHandleProps?: Record<string, any>;
  className?: string;
}) {
  const config = TYPE_CONFIG[method.payment_type] ?? {
    icon: CreditCard,
    label: method.payment_type,
    color: "text-gray-600",
    bg: "bg-gray-50 dark:bg-gray-900",
  };
  const Icon = config.icon;

  // Which storefront this method is assigned to. Only shown for multi-store
  // tenants — single-store tenants have nothing to disambiguate.
  const { data: stores = [] } = useStoreList();
  const isMultiStore = stores.length >= 2;
  const assignedStore = method.tenant
    ? stores.find((s) => s.storefront_key === method.tenant)
    : null;

  return (
    <div
      className={`flex items-center gap-3 rounded-md bg-muted/30 p-4 transition-colors hover:bg-muted/50 ${className}`}
    >
      {/* Drag handle */}
      <button
        className="cursor-grab touch-none text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
        {...dragHandleProps}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Icon badge */}
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded ${config.bg}`}
      >
        <Icon className={`h-5 w-5 ${config.color}`} />
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{config.label}</p>
          {isMultiStore &&
            (assignedStore ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                <span
                  className={cn(
                    "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                    getStoreColor(assignedStore.color_index)
                  )}
                />
                {assignedStore.label}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                ทุกร้าน / All
              </span>
            ))}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {getMethodSummary(method)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {onToggle && (
          <Switch
            checked={method.enabled === 1}
            onCheckedChange={() => onToggle()}
            aria-label={`${method.enabled ? "Disable" : "Enable"} ${config.label}`}
          />
        )}
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onEdit}
            className="h-8 w-8 rounded-sm"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-8 w-8 rounded-sm text-red-500 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function getMethodSummary(method: PaymentMethod): string {
  switch (method.payment_type) {
    case "Bank Transfer":
      return method.bank_name
        ? `${method.bank_name} — ${method.account_number ?? ""}`
        : "Not configured";
    case "Stripe":
      return method.stripe_publishable_key ? "Keys configured" : "Not configured";
    case "COD":
      return method.cod_max_amount
        ? `Max ฿${Number(method.cod_max_amount).toLocaleString()}`
        : "No limit";
    case "PaySolution":
      return method.paysolution_api_key
        ? `${method.paysolution_environment ?? "Sandbox"} — keys configured`
        : "Not configured";
    default:
      return "";
  }
}
